import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  /**
   * [A15-21/A09-06] Czwarty argument `options` jest OPCJONALNY i dokładany, nie zamieniający:
   * `{ actionLabel, onAction }` dorysowuje w dymku przycisk akcji (np. „Otwórz").
   *
   * Powód: Layout przy powiadomieniu o zadaniu/Kanbanie sam wywoływał `navigate(...)` i wyrzucał
   * użytkownika z bieżącej strony. Automatyczną nawigację usuwamy, ale droga „jednym kliknięciem"
   * ma zostać — teraz decyduje o niej użytkownik. Wszystkie dotychczasowe wywołania
   * `showToast(msg, type, duration)` działają bez zmian.
   */
  const showToast = useCallback((message, type = "info", duration = 5, options = null) => {
    const id = Date.now() + Math.random();
    const actionLabel = options?.actionLabel;
    const onAction = typeof options?.onAction === "function" ? options.onAction : null;
    setToasts((prevToasts) => [...prevToasts, { id, message, type, duration, actionLabel, onAction }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback(
    (message, duration) => {
      showToast(message, "success", duration);
    },
    [showToast]
  );

  const error = useCallback(
    (message, duration) => {
      showToast(message, "error", duration);
    },
    [showToast]
  );

  const info = useCallback(
    (message, duration) => {
      showToast(message, "info", duration);
    },
    [showToast]
  );

  const warning = useCallback(
    (message, duration) => {
      showToast(message, "warning", duration);
    },
    [showToast]
  );

  // Listen for concurrency conflict events from apiService interceptor
  useEffect(() => {
    const handleConflict = (e) => {
      const { message } = e.detail || {};
      showToast(message || "Dane zostały zmienione przez innego użytkownika.", "warning", 10);
    };
    window.addEventListener("concurrency-conflict", handleConflict);
    return () => window.removeEventListener("concurrency-conflict", handleConflict);
  }, [showToast]);

  // Stała tożsamość obiektu kontekstu — bez tego każdy toast unieważniał
  // useCallback-i konsumentów zależne od toast (ryzyko pętli ładowania).
  const contextValue = useMemo(
    () => ({ showToast, success, error, info, warning }),
    [showToast, success, error, info, warning]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

/* [PIN-ZARZAD poprawka 2 2026-09-28] Wersja bez wyjątku — dla bramek i okien montowanych także poza dostawcą (testy,
   ekran logowania). Zwraca `null`, gdy dostawcy nie ma; wołający sprawdza `toast?.showToast`. */
export const useToastOpcjonalny = () => useContext(ToastContext);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <ToastMessage
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={removeToast}
          duration={toast.duration}
          actionLabel={toast.actionLabel}
          onAction={toast.onAction}
        />
      ))}
    </div>
  );
};

const ToastMessage = ({ id, message, type, onClose, duration, actionLabel, onAction }) => {
  const DURATION = (duration ?? 5) * 1000;
  const [isPaused, setIsPaused] = useState(false);
  const [isDisappearing, setIsDisappearing] = useState(false);
  const timerRef = useRef(null);
  const remainingTimeRef = useRef(DURATION);
  const startTimeRef = useRef(null);

  const internalClose = useCallback(() => {
    if (isDisappearing) return;

    setIsPaused(true);
    clearTimeout(timerRef.current);
    setIsDisappearing(true);
  }, [isDisappearing]);

  const pause = useCallback(() => {
    if (isPaused || isDisappearing) return;
    clearTimeout(timerRef.current);
    const timeElapsed = Date.now() - startTimeRef.current;
    remainingTimeRef.current -= timeElapsed;
    setIsPaused(true);
  }, [isPaused, isDisappearing]);

  const resume = useCallback(() => {
    if (!isPaused || isDisappearing) return;
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(internalClose, remainingTimeRef.current);
    setIsPaused(false);
  }, [isPaused, isDisappearing, internalClose]);

  useEffect(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(internalClose, DURATION);
    return () => clearTimeout(timerRef.current);
  }, [internalClose, DURATION]);

  const handleAnimationEnd = (e) => {
    if (e.animationName === "toast-slide-out") {
      onClose(id);
    }
  };

  const typeClass = `toast-${type}`;

  return (
    <div
      className={`toast ${typeClass} ${
        isDisappearing ? "is-disappearing" : ""
      }`}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onAnimationEnd={handleAnimationEnd}
    >
      <div className="toast-icon"></div>
      <div className="toast-content">
        {/* Zabezpieczenie: gdy ktoś poda obiekt (np. ProblemDetails z API) React wywaliłby całą stronę
            ("Objects are not valid as a React child" → biały ekran). Zawsze renderujemy tekst. */}
        <p>{typeof message === "string"
          ? message
          : (message?.message || message?.title || message?.detail || "Wystąpił błąd.")}</p>
        <div className="toast-timer-bar-wrapper">
          <div
            className="toast-timer-bar"
            style={{
              animationDuration: `${DURATION}ms`,
              animationPlayState: isPaused ? "paused" : "running",
            }}
          ></div>
        </div>
      </div>
      {/* [A15-21/A09-06] Przejście do celu powiadomienia — świadome kliknięcie zamiast
          wymuszonej nawigacji w chwili przyjścia zdarzenia SignalR. */}
      {actionLabel && onAction && (
        <button
          type="button"
          className="toast-action-btn"
          onClick={() => {
            onAction();
            internalClose();
          }}
        >
          {actionLabel}
        </button>
      )}
      <button onClick={internalClose} className="toast-close-btn">
        &times;
      </button>
    </div>
  );
};
